import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Icon } from "@iconify/react";
import { Modal, Button, Form } from "react-bootstrap";
import { createErpSocket } from "../utils/socketIo";
import toast from "react-hot-toast";
import { formatDateToDDMMYYYY } from "./const";
import { useNavigate } from "react-router-dom";
import JangadReceiveUpdateModal from "../components/JangadReceiveUpdateModal";
import JangadPdfModal from "../components/JangadPdfModal";
import Pagination from "../components/Pagination";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import useIsCurrentFinancialYear, {
  FY_ADD_DISABLED_TITLE,
} from "../hooks/useIsCurrentFinancialYear";
import { listJangads, showJangad, isErpApiSuccess } from "../erpClient";
import {
  mapJangadShowResponseToLegacyOrder,
  mapJangadListRowToLegacyOrder,
  extractJangadListRows,
} from "../utils/jangadsErpAdapter";
import { parseApiPagination } from "../utils/parseApiPagination";
import { useVisitedPageCache, visitedPageKey } from "../utils/visitedPageCache";

function JangadTable({ masterGoldRate, masterSilverRate }) {
  const { buttonClass, tableClass, cardClass } = useButtonTableStyle();
  const isCurrentFinancialYear = useIsCurrentFinancialYear();
  const [orders, setOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [viewDetailLoading, setViewDetailLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState({});
  const [showReceiveUpdateModal, setShowReceiveUpdateModal] = useState(false);
  const [receiveUpdateOrderData, setReceiveUpdateOrderData] = useState(null);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [selectedOrderForPdf, setSelectedOrderForPdf] = useState(null);
  const [socket, setSocket] = useState(null);
  const navigate = useNavigate();

  const canList = hasPermission(PERMISSIONS.JANGAD);
  const canCreate = hasPermission(PERMISSIONS.ADD_JANGAD);
  const canEditAndSettle = hasPermission(PERMISSIONS.EDIT_JANGAD);

  // Normalize delivery date to DD/MM/YYYY even if backend sends time
  const formatDeliveryDate = (value) => {
    if (!value) return "N/A";
    try {
      // Handle different date formats without timezone conversion
      let dateStr = String(value);

      // Extract date part from datetime strings
      if (dateStr.includes("T") || dateStr.includes(" ")) {
        dateStr = dateStr.split(/[T ]/)[0];
      }

      // Parse as YYYY-MM-DD and format as DD/MM/YYYY
      const parts = dateStr.split("-");
      if (parts.length === 3) {
        const [year, month, day] = parts;
        return `${day}/${month}/${year}`;
      }

      return formatDateToDDMMYYYY(dateStr);
    } catch (e) {
      try {
        return formatDateToDDMMYYYY(value);
      } catch (e2) {
        return "N/A";
      }
    }
  };

  const pageCache = useVisitedPageCache();

  const fetchOrders = useCallback(
    async (page = 1, limit = 10, search = "", force = false) => {
      const cacheKey = visitedPageKey([page, limit, search.trim()]);
      if (force) pageCache.clear();
      const cached = force ? null : pageCache.read(cacheKey);
      if (cached) {
        setOrders(cached.items);
        setTotalCount(cached.total);
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        const listRes = await listJangads({
          search: search.trim(),
          page,
          limit,
        });

        const rows = extractJangadListRows(listRes);

        if (!isErpApiSuccess(listRes)) {
          setOrders([]);
          setTotalCount(0);
          toast.error(listRes?.message || "Failed to load jangad orders");
          return;
        }

        const mapped = rows
          .map((r) => mapJangadListRowToLegacyOrder(r))
          .filter(Boolean);

        mapped.sort((a, b) => Number(b.id) - Number(a.id));
        const total = parseApiPagination(listRes, mapped.length).total;
        setOrders(mapped);
        setTotalCount(total);
        pageCache.write(cacheKey, { items: mapped, total });
      } catch (error) {
        setOrders([]);
        setTotalCount(0);
        toast.error(
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error.message ||
          "Failed to fetch jangad orders",
        );
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    const timeoutId = setTimeout(
      () => {
        fetchOrders(currentPage, itemsPerPage, searchQuery);
      },
      searchQuery ? 200 : 0,
    );
    return () => clearTimeout(timeoutId);
  }, [currentPage, itemsPerPage, searchQuery, fetchOrders]);

  // Listen for API URL changes and refetch data
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log(
        "API URL changed in JangadTable, refetching orders...",
        event.detail,
      );
      setCurrentPage(1);
      fetchOrders(1, itemsPerPage, searchQuery, true);
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, [itemsPerPage, searchQuery, fetchOrders]);

  // Socket.IO listener for real-time updates
  useEffect(() => {
    const newSocket = createErpSocket();
    setSocket(newSocket);

    // Connection event logging
    newSocket.on("connect", () => {
      // console.log("JangadTable: Connected to socket server", newSocket.id);
    });

    newSocket.on("disconnect", (reason) => {
      // console.log("JangadTable: Disconnected from socket server:", reason);
    });

    newSocket.on("connect_error", (error) => {
      console.error("JangadTable: Socket connection error:", error);
    });

    newSocket.on("jangad_updated", (data) => {
      const jangadPayload = data?.jangad;
      if (!jangadPayload) return;

      const updatedOrder = mapJangadShowResponseToLegacyOrder({
        data: { jangad_document: jangadPayload },
      });

      if (!updatedOrder) return;

      setOrders((prev) => {
        const existingIdx = prev.findIndex(
          (o) => String(o.id) === String(updatedOrder.id),
        );
        if (existingIdx === -1) return [updatedOrder, ...prev];

        const next = [...prev];
        next[existingIdx] = {
          ...prev[existingIdx],
          ...updatedOrder,
          jangad_entries: updatedOrder.jangad_entries,
        };
        return next;
      });
    });

    newSocket.on("jangad_created", (data) => {
      const jangadPayload = data?.jangad;
      if (!jangadPayload) return;

      const newOrder = mapJangadShowResponseToLegacyOrder({
        data: { jangad_document: jangadPayload },
      });
      if (!newOrder) return;

      setOrders((prev) => {
        const exists = prev.some(
          (order) => String(order.id) === String(newOrder.id),
        );
        if (exists) {
          return prev.map((order) =>
            String(order.id) === String(newOrder.id) ? newOrder : order,
          );
        }
        return [newOrder, ...prev];
      });
    });

    return () => {
      newSocket.off("jangad_updated");
      newSocket.off("jangad_created");
      newSocket.disconnect();
    };
  }, []);

  // Handle item selection
  const handleItemSelect = (orderId, itemId, isSelected) => {
    setSelectedItems((prev) => {
      const newSelection = { ...prev };
      if (!newSelection[orderId]) {
        newSelection[orderId] = {};
      }

      if (isSelected) {
        newSelection[orderId][itemId] = true;
      } else {
        delete newSelection[orderId][itemId];
        if (Object.keys(newSelection[orderId]).length === 0) {
          delete newSelection[orderId];
        }
      }

      return newSelection;
    });
  };

  // Check if an item is selected
  const isItemSelected = (orderId, itemId) => {
    return Boolean(selectedItems[orderId]?.[itemId]);
  };

  // Check if an item is already received (status = 2 means received)
  const isItemReceived = (item) => {
    return item.status === "2" || item.status === 2;
  };

  // Get selected items count for display (only non-received items)
  const getSelectedItemsCount = () => {
    let count = 0;
    Object.entries(selectedItems).forEach(([orderId, orderItems]) => {
      const order = orders.find((o) => String(o.id) === String(orderId));
      if (order && order.jangad_entries) {
        Object.keys(orderItems).forEach((itemId) => {
          const item = order.jangad_entries.find(
            (e) => String(e.id) === String(itemId),
          );
          // Only count if item is not already received
          if (item && !isItemReceived(item)) {
            count++;
          }
        });
      }
    });
    return count;
  };

  // Helper function to get name based on group_type and group_id
  const getNameByGroupType = (order) => {
    // Return name directly from order object as per new API response
    if (order.name) return order.name;
    if (order.company_name) return order.company_name;
    if (order.karigar_name) return order.karigar_name;
    if (order.visitor_names) return order.visitor_names;
    return "N/A";
  };

  const getMobileByGroupType = (order) => {
    const mobile =
      order?.mobile ||
      order?.contact_number ||
      order?.customer_mobile ||
      order?.visitor_mobiles ||
      order?.supplier_mobile ||
      "";
    const trimmed = String(mobile).trim();
    return trimmed || "N/A";
  };

  const sortedOrders = [...orders].sort((a, b) => {
    if (a.created_at && b.created_at) {
      return new Date(b.created_at) - new Date(a.created_at);
    }
    return Number(b.id) - Number(a.id);
  });

  const displayedOrders = sortedOrders;

  const handlePageChange = (newPage) => {
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // View modal: load full document with show() for one id (weights, remarks, etc.)
  const handleViewClick = async (order) => {
    const docId = order?.id;
    if (docId == null || docId === "") {
      toast.error("Missing jangad id");
      return;
    }
    setSelectedOrder(order);
    setShowViewModal(true);
    setViewDetailLoading(true);
    try {
      const res = await showJangad({ id: docId });
      if (!isErpApiSuccess(res)) {
        toast.error(res?.message || "Failed to load jangad details");
        return;
      }
      const full = mapJangadShowResponseToLegacyOrder(res);
      if (full) {
        setSelectedOrder(full);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error.message ||
        "Failed to load jangad details",
      );
    } finally {
      setViewDetailLoading(false);
    }
  };

  // Add stub for Edit button
  const handleEditClick = (order) => {
    navigate("/editJangad", {
      state: {
        JangadOrderId: order.id,
        JangadOrderData: order,
      },
    });
  };

  // Navigate to Sales with jangad order data
  const handleMoveToSales = (order) => {
    const mappedOrderEntries = (order.jangad_entries || []).map(
      (item, index) => {
        const productGroupName =
          item.product_group_name || item.product_group || "";

        return {
          ...item,
          product_group: productGroupName,
          product_group_id: item.product_group,
          design: item.design || "",
          size: item.size || "",
          pcs: item.pcs || "",
          gwt: item.current_weight || item.gwt || "",
          lwt: item.lwt || "",
          nwt: item.exp_weight || item.nwt || "",
          lbr_percent: item.lbr_percentage || "",
          lbr_rate: item.lbr_rate || "",
          lbr_amt: item.lbr_amount || "",
          othamt: item.other_amount || "",
          totalamt: item.totalamt || item.final_amount || "",
          charges: item.Jangad_order_charges || [],
          tag_no: item.tag_no || "",
        };
      },
    );

    const customOrderData = {
      ...order,
      user_id: order.group_id || order.by_user,
      exp_delivery: order.exp_delivery || "",
      final_amount: order.final_amount || "",
      round_off: order.round_off || "",
      note: order.note || "",
      co_number: order.voucher_number || "",
    };

    const navigationState = {
      JangadOrderId: order.id,
      JangadOrderData: order,
      customOrderId: null,
      customOrderTable: mappedOrderEntries,
      customOrderData: customOrderData,
      customOrderPayments: [],
      isFromJangadOrder: true,
    };

    navigate("/sales", {
      state: navigationState,
    });
  };

  // Handle bulk receive & update for selected items
  const handleBulkReceiveUpdate = () => {
    if (getSelectedItemsCount() === 0) {
      toast.error("Please select at least one item");
      return;
    }

    const selectedItemsData = [];
    Object.keys(selectedItems).forEach((orderId) => {
      const order = orders.find((o) => String(o.id) === String(orderId));
      if (order && selectedItems[orderId]) {
        const selectedItemIds = Object.keys(selectedItems[orderId]);
        const filteredItems =
          order.jangad_entries?.filter((item) =>
            selectedItemIds.includes(String(item.id)),
          ) || [];

        if (filteredItems.length > 0) {
          const mappedItems = filteredItems.map((item) => ({
            ...item,
            product_group: item.product_group,
            product_group_name: item.product_group_name || "",
            design: item.design || "",
            size: item.size || "",
            pcs: item.pcs || "",
            gwt: item.gwt || "",
            lwt: item.lwt || "",
            nwt: item.nwt || "",
            totalamt: item.totalamt || item.final_amount || "",
          }));

          selectedItemsData.push({
            ...order,
            custom_order_entry: mappedItems,
            jangad_entries: filteredItems,
            isJangadOrder: true,
          });
        }
      }
    });

    if (selectedItemsData.length > 0) {
      let combinedOrderData;

      if (selectedItemsData.length === 1) {
        combinedOrderData = selectedItemsData[0];
      } else {
        combinedOrderData = {
          ...selectedItemsData[0],
          custom_order_entry: selectedItemsData.flatMap(
            (order) => order.custom_order_entry,
          ),
          jangad_entries: selectedItemsData.flatMap(
            (order) => order.jangad_entries,
          ),
          id: "bulk_receive_" + Date.now(),
          isBulkReceive: true,
          isJangadOrder: true,
        };
      }

      setReceiveUpdateOrderData(combinedOrderData);
      setShowReceiveUpdateModal(true);
    }
  };

  // Handle receive & update success
  const handleReceiveUpdateSuccess = () => {
    setSelectedItems({});
    fetchOrders(currentPage, itemsPerPage, searchQuery, true);
    setShowReceiveUpdateModal(false);
    setReceiveUpdateOrderData(null);
  };

  // Handle receive & update modal close without success
  const handleReceiveUpdateModalClose = () => {
    setShowReceiveUpdateModal(false);
    setReceiveUpdateOrderData(null);
  };

  return (
    <>
      <style>
        {`
          .item-checkbox {
            opacity: 0;
            transition: opacity 0.2s ease;
          }
          
          .item-column:hover .item-checkbox {
            opacity: 1;
          }
          
          .item-row:hover .item-checkbox {
            opacity: 1;
          }
          
          .item-checkbox:checked {
            opacity: 1 !important;
          }

          .item-scroll-container {
            padding: 4px;
            scrollbar-width: thin;
            scrollbar-color: #c1c1c1 #f1f1f1;
          }

          .item-scroll-container::-webkit-scrollbar {
            width: 6px;
          }

          .item-scroll-container::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 3px;
          }

          .item-scroll-container::-webkit-scrollbar-thumb {
            background: #c1c1c1;
            border-radius: 3px;
          }

          .item-scroll-container::-webkit-scrollbar-thumb:hover {
            background: #a8a8a8;
          }
        `}
      </style>
      <div className={`body-wrapper ${cardClass}`}>
        <div className="container-fluid">
          <div
            className={`card w-100 position-relative overflow-hidden ${cardClass}`}
          >
            <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
              <h4 className="card-title mb-0">Jangad Table</h4>
              {canCreate && (
                <button
                  className={`btn btn-primary ${buttonClass}`}
                  disabled={!isCurrentFinancialYear}
                  title={
                    !isCurrentFinancialYear ? FY_ADD_DISABLED_TITLE : undefined
                  }
                  onClick={() => {
                    if (!isCurrentFinancialYear) return;
                    navigate("/addjangad");
                  }}
                >
                  Add Jangad
                </button>
              )}
            </div>

            <div className="card-body p-4">
              {/* Search and Filter Section */}
              <div className="d-flex justify-content-between mb-3">
                <input
                  type="text"
                  className={`form-control ${buttonClass}`}
                  style={{ maxWidth: "220px" }}
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                />
                <div className="d-flex align-items-center gap-2">
                  {canEditAndSettle && getSelectedItemsCount() > 0 && (
                    <button
                      className={`btn btn-primary ${buttonClass}`}
                      onClick={handleBulkReceiveUpdate}
                    >
                      Receive & Update ({getSelectedItemsCount()})
                    </button>
                  )}
                </div>
              </div>

              {/* Table */}
              <div className={`table-responsive mb-4 border ${cardClass}`}>
                <table className={`table mb-0 align-middle ${tableClass}`}>
                  <thead className="text-dark fs-4">
                    <tr>
                      <th style={{ minWidth: "80px" }}>Voucher</th>
                      <th style={{ minWidth: "100px" }}>Created By</th>
                      <th style={{ minWidth: "120px" }}>Name</th>
                      <th style={{ minWidth: "200px", maxWidth: "250px" }}>
                        Item
                      </th>
                      <th style={{ minWidth: "100px" }}>Delivery</th>
                      <th style={{ minWidth: "120px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td colSpan="6" className="text-center">
                          Loading...
                        </td>
                      </tr>
                    ) : displayedOrders.length > 0 ? (
                      displayedOrders.map((order) => {
                        return (
                          <tr key={order.id}>
                            <td>{order.voucher_number || "N/A"}</td>
                            <td>{order.employee_name || "N/A"}</td>
                            <td>{getNameByGroupType(order)}</td>
                            <td
                              className="item-column"
                              style={{ maxWidth: "250px", minWidth: "200px" }}
                            >
                              {(() => {
                                const items = order.jangad_entries;
                                const hasItems =
                                  items &&
                                  Array.isArray(items) &&
                                  items.length > 0;
                                if (!hasItems) return "No items";
                                // Cap height after ~3 items (~38px each)
                                const needsScroll = items.length > 3;
                                return (
                                  <div
                                    className={
                                      needsScroll ? "item-scroll-container" : undefined
                                    }
                                    style={{
                                      maxWidth: "100%",
                                      ...(needsScroll
                                        ? {
                                          maxHeight: "120px",
                                          overflowY: "auto",
                                          overflowX: "hidden",
                                        }
                                        : {}),
                                    }}
                                  >
                                    {items.map((item, index) => (
                                      <div
                                        key={item.id || index}
                                        className="mb-1 d-flex align-items-start item-row"
                                        style={{ maxWidth: "100%" }}
                                      >
                                        <input
                                          type="checkbox"
                                          className="form-check-input me-2 item-checkbox"
                                          style={{
                                            flexShrink: 0,
                                            marginTop: "2px",
                                            opacity: isItemReceived(item)
                                              ? 0.5
                                              : 1,
                                          }}
                                          checked={isItemSelected(
                                            order.id,
                                            item.id,
                                          )}
                                          disabled={isItemReceived(item)}
                                          onChange={(e) => {
                                            if (!isItemReceived(item)) {
                                              handleItemSelect(
                                                order.id,
                                                item.id,
                                                e.target.checked,
                                              );
                                            }
                                          }}
                                        />
                                        <span
                                          style={{
                                            wordWrap: "break-word",
                                            overflowWrap: "break-word",
                                            wordBreak: "break-word",
                                            whiteSpace: "normal",
                                            lineHeight: "1.3",
                                            fontSize: "0.9em",
                                            flex: 1,
                                            opacity: isItemReceived(item)
                                              ? 0.6
                                              : 1,
                                            color: isItemReceived(item)
                                              ? "#6c757d"
                                              : "inherit",
                                          }}
                                        >
                                          {item.tag_no
                                            ? `${item.tag_no} - ${item.product_group_name ||
                                            item.product_type ||
                                            item.product_group ||
                                            "Product Type"
                                            }`
                                            : item.product_group_name ||
                                            item.product_type ||
                                            item.product_group ||
                                            "Product Type"}
                                          {isItemReceived(item) && (
                                            <span
                                              className="badge bg-success-subtle text-success ms-2"
                                              style={{ fontSize: "0.7em" }}
                                            >
                                              ✓ Received
                                            </span>
                                          )}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                );
                              })()}
                            </td>
                            <td>{formatDeliveryDate(order.exp_delivery)}</td>
                            <td>
                              <div className="d-flex gap-2">
                                {canList && (
                                  <button
                                    className={`btn btn-primary btn-sm ${buttonClass}`}
                                    onClick={() => handleViewClick(order)}
                                    title="View Order"
                                  >
                                    <Icon
                                      icon="solar:eye-bold"
                                      width="16"
                                      height="16"
                                    />
                                  </button>
                                )}
                                <button
                                  className={`btn btn-primary btn-sm ${buttonClass}`}
                                  onClick={() => {
                                    setSelectedOrderForPdf(order);
                                    setShowPdfModal(true);
                                  }}
                                  title="PDF Preview"
                                >
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M4.172 3.172C3 4.343 3 6.229 3 10v4c0 3.771 0 5.657 1.172 6.828S7.229 22 11 22h2c3.771 0 5.657 0 6.828-1.172S21 17.771 21 14v-4c0-3.771 0-5.657-1.172-6.828S16.771 2 13 2h-2C7.229 2 5.343 2 4.172 3.172M7.25 8A.75.75 0 0 1 8 7.25h8a.75.75 0 0 1 0 1.5H8A.75.75 0 0 1 7.25 8m0 4a.75.75 0 0 1 .75-.75h8a.75.75 0 0 1 0 1.5H8a.75.75 0 0 1-.75-.75M8 15.25a.75.75 0 0 0 0 1.5h5a.75.75 0 0 0 0-1.5z"
                                      clipRule="evenodd"
                                    ></path>
                                  </svg>
                                </button>
                                {(() => {
                                  if (!canEditAndSettle) return null;

                                  const hasAssignedKarigar = Array.isArray(
                                    order.jangad_entries,
                                  )
                                    ? order.jangad_entries.some(
                                      (e) =>
                                        e && (e.karigar_id || e.given_emp_id),
                                    )
                                    : false;

                                  const anyItemReturned =
                                    Array.isArray(order.jangad_entries) &&
                                    order.jangad_entries.some((e) =>
                                      isItemReceived(e),
                                    );

                                  return hasAssignedKarigar ? (
                                    <button
                                      className={`btn btn-primary btn-sm ${buttonClass}`}
                                      onClick={() => handleMoveToSales(order)}
                                      title="Move to Sales"
                                    >
                                      <Icon
                                        icon="solar:cart-large-bold"
                                        width="16"
                                        height="16"
                                      />
                                    </button>
                                  ) : (
                                    <button
                                      className={`btn btn-sm ${anyItemReturned
                                          ? `btn-secondary ${buttonClass}`
                                          : `btn-primary ${buttonClass}`
                                        }`}
                                      onClick={() =>
                                        anyItemReturned
                                          ? null
                                          : handleEditClick(order)
                                      }
                                      title={
                                        anyItemReturned
                                          ? "Some items already received - Cannot edit"
                                          : "Edit"
                                      }
                                      disabled={anyItemReturned}
                                    >
                                      <Icon
                                        icon="solar:pen-bold"
                                        width="16"
                                        height="16"
                                      />
                                    </button>
                                  );
                                })()}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center">
                          No jangad orders found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <Pagination
                totalItems={totalCount}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                itemsPerPageOptions={[10, 20, 30, 50, 100]}
              />
            </div>
          </div>
        </div>

        {/* View Jangad Order Modal */}
        <Modal
          show={showViewModal}
          onHide={() => {
            setShowViewModal(false);
            setViewDetailLoading(false);
          }}
          centered
          size="xl"
        >
          <Modal.Header closeButton>
            <div className="d-flex w-100 align-items-center justify-content-between">
              <Modal.Title className="mb-0">Jangad Order Details</Modal.Title>
              {selectedOrder && (
                <span
                  className="badge bg-info-subtle text-info"
                  style={{ fontSize: "1em" }}
                >
                  {selectedOrder.voucher_number || selectedOrder.id || "N/A"}
                </span>
              )}
            </div>
          </Modal.Header>
          {selectedOrder && (
            <div
              className="ms-4 mt-2 mb-0"
              style={{
                fontSize: "1.08em",
                fontWeight: 500,
                textAlign: "left",
                color: "#212529",
              }}
            >
              <div>Name: {getNameByGroupType(selectedOrder)}</div>
              <div>Mobile: {getMobileByGroupType(selectedOrder)}</div>
            </div>
          )}
          <Modal.Body>
            {viewDetailLoading ? (
              <div className="text-center py-4 text-muted">
                Loading details…
              </div>
            ) : selectedOrder &&
              selectedOrder.jangad_entries &&
              Array.isArray(selectedOrder.jangad_entries) &&
              selectedOrder.jangad_entries.length > 0 ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                }}
              >
                {selectedOrder.jangad_entries.map((item, idx) => {
                  // Check if item is received (status = 2 means received)
                  const isReceived = item.status === "2" || item.status === 2;

                  return (
                    <div
                      key={idx}
                      className={`mb-3 p-3 border rounded jangad-detail-card${isReceived ? " is-received" : ""
                        }`}
                      style={{
                        color: "var(--theme-text-primary, #212529)",
                      }}
                    >
                      {/* Header with Product Group and Status */}
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0">
                          <strong>Product Group:</strong>{" "}
                          {item.product_group_name ||
                            item.product_type ||
                            item.product_group ||
                            "N/A"}
                        </h6>
                        {isReceived && (
                          <span className="badge bg-success-subtle text-success">
                            ✓ Received
                          </span>
                        )}
                      </div>

                      {/* Original Item Details */}
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <div className="mb-2">
                            <strong>Tag No:</strong> {item.tag_no || "-"}
                          </div>
                          <div className="mb-2">
                            <strong>Pieces:</strong> {item.pcs || "-"}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-2">
                            <strong>GWT:</strong>{" "}
                            {item.gwt || item.current_weight || "-"}
                          </div>
                          {/* <div className="mb-2">
                            <strong>LWT:</strong> {item.lwt || "-"}
                          </div> */}
                          <div className="mb-2">
                            <strong>NWT:</strong>{" "}
                            {item.nwt || item.exp_weight || "-"}
                          </div>
                        </div>
                      </div>

                      {/* Received Information - Only show if received */}
                      {isReceived && (
                        <>
                          <hr className="my-3" />
                          <div className="p-3 border rounded">
                            <h6 className="mb-3">
                              <strong>Received Details</strong>
                            </h6>

                            <div className="row">
                              <div className="col-md-6">
                                {item.received_date && (
                                  <div className="mb-2">
                                    <strong>Received Date:</strong>{" "}
                                    {formatDateToDDMMYYYY(item.received_date)}
                                  </div>
                                )}
                                <div className="mb-2">
                                  <strong>Received GWT:</strong>{" "}
                                  <span className="text-success fw-bold">
                                    {item.received_gwt !== undefined &&
                                      item.received_gwt !== null &&
                                      String(item.received_gwt).trim() !== ""
                                      ? item.received_gwt
                                      : item.gwt !== undefined &&
                                        item.gwt !== null &&
                                        String(item.gwt).trim() !== ""
                                        ? item.gwt
                                        : item.current_weight !== undefined &&
                                          item.current_weight !== null &&
                                          String(
                                            item.current_weight,
                                          ).trim() !== ""
                                          ? item.current_weight
                                          : "-"}
                                  </span>
                                </div>
                                <div className="mb-2">
                                  <strong>Received NWT:</strong>{" "}
                                  <span className="text-success fw-bold">
                                    {item.received_nwt !== undefined &&
                                      item.received_nwt !== null &&
                                      String(item.received_nwt).trim() !== ""
                                      ? item.received_nwt
                                      : item.nwt !== undefined &&
                                        item.nwt !== null &&
                                        String(item.nwt).trim() !== ""
                                        ? item.nwt
                                        : item.exp_weight !== undefined &&
                                          item.exp_weight !== null &&
                                          String(item.exp_weight).trim() !==
                                          ""
                                          ? item.exp_weight
                                          : "-"}
                                  </span>
                                </div>
                              </div>
                              <div className="col-md-6">
                                {item.received_no_of_visitors && (
                                  <div className="mb-2">
                                    <strong>No. of Visitors:</strong>{" "}
                                    {item.received_no_of_visitors}
                                  </div>
                                )}
                                {item.received_visitor_names && (
                                  <div className="mb-2">
                                    <strong>Visitor Names:</strong>{" "}
                                    {item.received_visitor_names}
                                  </div>
                                )}
                                {item.received_visitor_mobiles && (
                                  <div className="mb-2">
                                    <strong>Visitor Mobiles:</strong>{" "}
                                    {item.received_visitor_mobiles}
                                  </div>
                                )}
                              </div>
                            </div>

                            {item.received_remarks && (
                              <div className="mt-2">
                                <strong>Remarks:</strong>
                                <div className="mt-1 p-2 bg-light rounded">
                                  {item.received_remarks}
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div>No product details available.</div>
            )}
          </Modal.Body>
        </Modal>

        {/* Jangad Receive & Update Modal */}
        <JangadReceiveUpdateModal
          show={showReceiveUpdateModal}
          onHide={handleReceiveUpdateModalClose}
          orderData={receiveUpdateOrderData}
          onReceiveSuccess={handleReceiveUpdateSuccess}
          masterGoldRate={masterGoldRate}
          masterSilverRate={masterSilverRate}
        />

        {/* Jangad PDF Preview Modal */}
        <JangadPdfModal
          show={showPdfModal}
          onHide={() => setShowPdfModal(false)}
          orderId={selectedOrderForPdf?.id}
        />
      </div>
    </>
  );
}

export default JangadTable;
